import { globalLoadingWithProgress } from "@redux/ui"
import { locData } from "@utils/localization"
import _ from "lodash"
import { prepareHeaders, type ErrorType } from "./api"
import { parseSSE } from "@utils/parseSSE"
import { BACKEND_URL } from "@/const"
import type { Dispatch } from "redux"
import type { TypedUseQuery } from "@reduxjs/toolkit/query/react"
import { useTranslation } from "react-i18next"

/**
 * `transformResponse` function for localizing response data based on
 * top-level `translations` path if one exists
 *
 * @param {Object} response 			Response data
 * @param {any} meta							baseQuery meta property
 * @param {Object} arg						Query args; `noLocalization` property disables localization for the query
 *
 * @returns {Object}							Response with paths modified
 */
export const locResponse = (response: any, _meta: unknown, arg?: any) => {
	if (arg?.noLocalization) {
		return response
	}
	return locData(response)
}
export const getProgressQueryFn =
	<T = unknown>(method: string, url: string | ((arg: any) => string), getBody: (arg: any) => unknown) =>
	async (arg: any, { getState, dispatch }: { getState: () => unknown; dispatch: Dispatch }) => {
		const headers = prepareHeaders(new Headers(), { getState })
		headers.set("Content-Type", "application/json")
		const res = await fetch(BACKEND_URL.concat(_.isFunction(url) ? url(arg) : url), {
			method,
			headers,
			body: JSON.stringify(getBody(arg)),
			credentials: "include",
		})
		if (res.status !== 200) {
			return { error: { status: res.status, data: await res.text() } as unknown as ErrorType }
		}

		const reader = res.body?.getReader()
		if (!reader) return { data: {} as T }

		const decoder = new TextDecoder("utf-8")
		let done = false
		let rec = {}
		while (!done) {
			const { done: doneReading, value } = await reader.read()
			done = doneReading
			const eventStreamData = decoder.decode(value, { stream: !done })
			parseSSE(eventStreamData).forEach((event) => {
				try {
					if (event.data === undefined) return
					if (event.event === "error") {
						throw new Error(JSON.parse(event.data))
					}
					if (event.event === "progress") {
						const { processed, total } = JSON.parse(event.data)
						dispatch(globalLoadingWithProgress(processed / total))
					}
					if (event.event === "done") {
						rec = JSON.parse(event.data)
					}
				} catch (error) {
					dispatch(globalLoadingWithProgress(false))
					throw error
				}
			})
		}
		dispatch(globalLoadingWithProgress(false))
		return { data: { ...rec } as T }
	}

/**
 * Injects i18n.language value into query arguments to ensure that distinct cache entries are maintained
 * for different languages.
 */
export const useLangRefetch = (useQueryHook: TypedUseQuery<any, any, any>, arg?: unknown, options?: Record<string, any>) => {
	const { i18n } = useTranslation()
	const lang = i18n.language
	let modifiedArgs: any = arg
	if (!arg) {
		modifiedArgs = { lang } // Argument is null or undefined
	} else {
		if (typeof arg === "object") {
			if (Array.isArray(arg)) {
				modifiedArgs = [...arg, lang] // Argument is array
			} else {
				if (!modifiedArgs.noLoc) {
					modifiedArgs = { ...arg, lang: lang } // Argument is object where `noLoc` is not enabled
				}
			}
		} else {
			console.warn(
				"`useLangRefetch` wrapper is used in an endpoint that has a primitive value that is not extensible as its argument"
			) // argument is not an array or object
		}
	}
	return useQueryHook(modifiedArgs, options)
}
